'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Maximize2, X, BookOpen, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  SHOPIFY_APP_URL,
  SHOPIFY_REDIRECT_URL,
  SHOPIFY_REQUIRED_SCOPES,
  SHOPIFY_TUTORIAL_ASSET_BASE_PATH,
} from '@/lib/shopify-constants';
import { cn } from '@/lib/utils';

const baseAssetPath = SHOPIFY_TUTORIAL_ASSET_BASE_PATH;

/**
 * Tutorial step type
 */
interface TutorialStep {
  title: string;
  instructions: string;
  image: string;
  copyableScopes?: boolean;
}

// ============================================================================
// Copy Button Component
// ============================================================================

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = React.useState(false);
  const { t } = useTranslation('integrations');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('toasts.copied', { label }));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('toasts.failedToCopy'));
    }
  };

  return (
    <div className="group relative">
      <div className="flex items-center gap-2 rounded-md border bg-gray-50 p-2">
        <code className="flex-1 overflow-x-auto whitespace-nowrap text-xs text-gray-700">{text}</code>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-6 w-6 shrink-0 p-0"
          title={t('shopifyTutorial.copyLabel', { label })}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

/**
 * Copyable fields section for scopes and redirect URL
 */
function CopyableFields({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('integrations');
  return (
    <div className={cn('space-y-3', compact ? 'mt-2' : 'mt-4')}>
      <div>
        <p className={cn('mb-1 font-medium text-gray-700', compact ? 'text-xs' : 'text-sm')}>
          {t('shopifyTutorial.scopesLabel')}
        </p>
        <CopyButton text={SHOPIFY_REQUIRED_SCOPES} label={t('shopifyTutorial.copyLabels.scopes')} />
      </div>
      <div>
        <p className={cn('mb-1 font-medium text-gray-700', compact ? 'text-xs' : 'text-sm')}>
          {t('shopifyTutorial.appUrlLabel')}
        </p>
        <CopyButton text={SHOPIFY_APP_URL} label={t('shopifyTutorial.copyLabels.appUrl')} />
      </div>
      <div>
        <p className={cn('mb-1 font-medium text-gray-700', compact ? 'text-xs' : 'text-sm')}>
          {t('shopifyTutorial.redirectUrlLabel')}
        </p>
        <CopyButton text={SHOPIFY_REDIRECT_URL} label={t('shopifyTutorial.copyLabels.redirectUrl')} />
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

interface ShopifySetupTutorialProps {
  className?: string;
  defaultExpanded?: boolean;
  /**
   * Tutorial variant:
   * - 'legacy': Custom App setup with API token (old method)
   * - 'oauth': Public App setup via Dev Dashboard (new method)
   */
  variant?: 'legacy' | 'oauth';
}

const LEGACY_STEP_IMAGES = [
  'shopify-tuto1.jpeg', 'shopify-tuto2.jpeg', 'shopify-tuto3.jpeg', 'shopify-tuto4.jpeg',
  'shopify-tuto5.jpeg', 'shopify-tuto6.jpeg', 'shopify-tuto7.jpeg', 'shopify-tuto8.jpeg', 'shopify-tuto9.jpeg',
];
const OAUTH_STEP_IMAGES = [
  'step1-apps.png', 'step2-develop-apps.png', 'step3-dev-dashboard.png', 'step4-create-app.png',
  'step5-scopes-redirect.png', 'step6-release.png', 'step7-credentials.png',
];

export function ShopifySetupTutorial({
  className,
  defaultExpanded = false,
  variant = 'legacy',
}: ShopifySetupTutorialProps) {
  const { t } = useTranslation('integrations');
  const [currentStep, setCurrentStep] = React.useState(1);
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const TUTORIAL_STEPS = React.useMemo(() => {
    const images = variant === 'oauth' ? OAUTH_STEP_IMAGES : LEGACY_STEP_IMAGES;
    const basePath = variant === 'oauth' ? `${baseAssetPath}/tutov2` : `${baseAssetPath}/tuto`;
    const ns = variant === 'oauth' ? 'shopifyTutorial.oauth' : 'shopifyTutorial.legacy';
    return images.map((img, i) => ({
      title: t(`${ns}.step${i + 1}Title`),
      instructions: t(`${ns}.step${i + 1}Instructions`),
      image: `${basePath}/${img}`,
      copyableScopes: variant === 'oauth' && i === 4,
    }));
  }, [variant, t]);

  const tutorialTitle = variant === 'oauth' ? t('shopifyTutorial.publicAppSetupGuide') : t('shopifyTutorial.shopifySetupGuide');

  const totalSteps = TUTORIAL_STEPS.length;
  const step = TUTORIAL_STEPS[currentStep - 1];

  const goToStep = (stepNumber: number) => {
    setCurrentStep(Math.max(1, Math.min(stepNumber, totalSteps)));
  };

  const nextStep = () => goToStep(currentStep + 1);
  const prevStep = () => goToStep(currentStep - 1);

  // Handle ESC key to close fullscreen
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // Prevent body scroll when fullscreen
  React.useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // Collapsed view
  if (!isExpanded) {
    return (
      <Card className={cn('h-fit border-blue-100 bg-blue-50/50', className)}>
        <CardContent className="p-4">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{t('shopifyTutorial.needHelp')}</h4>
              <p className="text-sm text-gray-600">{t('shopifyTutorial.stepByStepGuide')}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="w-full border-blue-200 bg-white hover:bg-blue-50"
            >
              {t('shopifyTutorial.viewTutorial')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fullscreen overlay
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b bg-gray-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">{tutorialTitle}</h2>
            <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
              {t('shopifyTutorial.stepOf', { current: currentStep, total: totalSteps })}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setIsFullscreen(false)} className="h-8 w-8 p-0">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Instructions */}
          <div className="flex w-80 shrink-0 flex-col border-r bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-lg font-bold text-white">
                {currentStep}
              </span>
              <span className="text-sm text-gray-500">{t('shopifyTutorial.stepOf', { current: currentStep, total: totalSteps })}</span>
            </div>

            <div className="mt-6">
              <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
              <p className="mt-3 text-gray-600">{step.instructions}</p>
              {/* Copyable fields for scopes step */}
              {'copyableScopes' in step && step.copyableScopes && <CopyableFields />}
            </div>

            {/* Progress dots */}
            <div className="mt-6 flex flex-wrap items-center gap-1.5">
              {TUTORIAL_STEPS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToStep(idx + 1)}
                  className={cn(
                    'h-2.5 rounded-full transition-all',
                    currentStep === idx + 1 ? 'w-8 bg-blue-600' : 'w-2.5 bg-gray-300 hover:bg-gray-400',
                  )}
                  aria-label={t('shopifyTutorial.goToStep', { step: idx + 1 })}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="mt-auto flex items-center gap-3 pt-6">
              <Button variant="outline" onClick={prevStep} disabled={currentStep === 1} className="flex-1">
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t('shopifyTutorial.previous')}
              </Button>
              <Button
                variant={currentStep === totalSteps ? 'default' : 'outline'}
                onClick={currentStep === totalSteps ? () => setIsFullscreen(false) : nextStep}
                className="flex-1"
              >
                {currentStep === totalSteps ? (
                  t('shopifyTutorial.done')
                ) : (
                  <>
                    {t('shopifyTutorial.next')}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Right: Image */}
          <div className="flex flex-1 items-center justify-center bg-gray-100 p-8">
            <div className="relative h-full w-full max-w-5xl overflow-hidden rounded-lg border bg-white shadow-lg">
              <img
                src={step.image}
                alt={t('shopifyTutorial.stepImageAlt', { step: currentStep, title: step.title })}
                className="h-full w-full object-contain"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Inline expanded view - compact vertical layout
  return (
    <Card className={cn('flex h-full flex-col overflow-hidden', className)}>
      {/* Header */}
      <CardHeader className="shrink-0 border-b bg-gray-50 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <BookOpen className="h-4 w-4 text-blue-600" />
            {t('shopifyTutorial.setupGuide')}
          </CardTitle>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsFullscreen(true)}
              className="h-7 w-7 p-0"
              title={t('shopifyTutorial.fullscreen')}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="h-7 w-7 p-0"
              title={t('shopifyTutorial.close')}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="flex flex-1 flex-col p-3">
        {/* Step info */}
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
            {currentStep}
          </span>
          <h3 className="flex-1 truncate text-sm font-medium text-gray-900">{step.title}</h3>
        </div>

        {/* Image */}
        <div
          className="group relative mb-2 flex-1 cursor-pointer overflow-hidden rounded-md border bg-gray-50"
          onClick={() => setIsFullscreen(true)}
        >
          <img
            src={step.image}
            alt={t('shopifyTutorial.stepImageAlt', { step: currentStep, title: step.title })}
            className="h-full w-full object-contain"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/10 group-hover:opacity-100">
            <div className="rounded-full bg-white/90 p-1.5 shadow-lg">
              <Maximize2 className="h-4 w-4 text-gray-700" />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <p className="mb-2 text-xs text-gray-600">{step.instructions}</p>

        {/* Copyable fields for scopes step */}
        {'copyableScopes' in step && step.copyableScopes && <CopyableFields compact />}

        {/* Progress & Navigation */}
        <div className="flex items-center justify-between gap-2">
          {/* Progress dots */}
          <div className="flex items-center gap-1">
            {TUTORIAL_STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToStep(idx + 1)}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  currentStep === idx + 1 ? 'w-3 bg-blue-600' : 'w-1.5 bg-gray-300 hover:bg-gray-400',
                )}
                aria-label={t('shopifyTutorial.goToStep', { step: idx + 1 })}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="h-7 px-2 text-xs"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-xs text-gray-500">
              {currentStep}/{totalSteps}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={nextStep}
              disabled={currentStep === totalSteps}
              className="h-7 px-2 text-xs"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
